import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CreditCard, MessageSquare, Printer, BarChart3, Globe, Truck, Smartphone, Zap, ShoppingCart } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import FooterSection from "@/components/landing/FooterSection";

const integrations = [
  {
    category: "Marketplaces",
    items: [
      {
        name: "iFood",
        desc: "Integração completa com o iFood. Receba pedidos direto no painel, sincronize seu cardápio bidirecionalmente e atualize status automaticamente. Cada restaurante conecta suas próprias credenciais via OAuth.",
        icon: ShoppingCart,
        status: "Disponível",
        details: ["Recebimento de pedidos em tempo real", "Sincronização bidirecional de cardápio", "Atualização automática de status", "Credenciais OAuth por restaurante", "Mapeamento de produtos ViaMenu ↔ iFood"],
      },
    ],
  },
  {
    category: "Pagamentos",
    items: [
      { name: "MercadoPago", desc: "Processe pagamentos via PIX, cartão de crédito e débito diretamente na plataforma.", icon: CreditCard, status: "Disponível" },
      { name: "PIX Automático", desc: "Geração automática de QR Code PIX para pagamentos instantâneos.", icon: Zap, status: "Disponível" },
    ],
  },
  {
    category: "Comunicação",
    items: [
      { name: "WhatsApp", desc: "Envie confirmações de pedido, atualizações de status e cupons diretamente pelo WhatsApp.", icon: MessageSquare, status: "Disponível" },
      { name: "E-mail Transacional", desc: "E-mails automáticos de confirmação, recuperação de senha e comunicações de marketing.", icon: Globe, status: "Disponível" },
    ],
  },
  {
    category: "Operação",
    items: [
      { name: "Impressoras Térmicas", desc: "Impressão automática de pedidos na cozinha via rede local.", icon: Printer, status: "Em breve" },
      { name: "Google Analytics", desc: "Acompanhe métricas de acesso ao cardápio digital e conversões.", icon: BarChart3, status: "Disponível" },
    ],
  },
  {
    category: "Delivery",
    items: [
      { name: "Google Maps", desc: "Cálculo automático de distância e taxa de entrega baseado na localização.", icon: Truck, status: "Em breve" },
      { name: "App do Entregador", desc: "Aplicativo dedicado para entregadores com rotas e atualizações em tempo real.", icon: Smartphone, status: "Em breve" },
    ],
  },
];

const Integracoes = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="pt-32 pb-16">
        <div className="container text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
              Integrações
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Conecte com as ferramentas que você <span className="text-gradient">já usa</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Integrações nativas com os principais serviços de pagamento, comunicação e logística do mercado.
            </p>
          </motion.div>
        </div>
      </section>

      {integrations.map((group, gi) => (
        <section key={group.category} className={`py-16 ${gi % 2 === 1 ? "bg-card/50" : ""}`}>
          <div className="container">
            <h2 className="text-2xl font-bold mb-8">{group.category}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {group.items.map((item, i) => (
                <motion.div
                  key={item.name}
                  className="flex gap-5 p-6 rounded-xl bg-gradient-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                >
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon className="h-6 w-6 text-primary" />
                  </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-lg">{item.name}</h3>
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                          item.status === "Disponível" 
                            ? "bg-primary/10 text-primary" 
                            : "bg-muted text-muted-foreground"
                        }`}>
                          {item.status}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                      {(item as any).details && (
                        <ul className="mt-3 space-y-1">
                          {(item as any).details.map((d: string) => (
                            <li key={d} className="text-xs text-muted-foreground flex items-center gap-2">
                              <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                              {d}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="py-24">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4">Precisa de uma integração específica?</h2>
          <p className="text-muted-foreground mb-8">Confira nossa API ou entre em contato com nosso time.</p>
          <div className="flex justify-center gap-4">
            <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground">
              <Link to="/api">Ver Documentação da API <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/contato">Falar com o Time</Link>
            </Button>
          </div>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default Integracoes;
